import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, FileText, Plus, LogOut, Check, X, Shield, Search, Users, Lock, CreditCard, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { walletAPI, quoteAPI } from '@/lib/api';
import WhatsAppFreightWidget from '@/components/WhatsAppFreightWidget';

// Credit costs for different actions
const CREDIT_COSTS = {
  sea_quote: 10,
  air_quote: 15,
  message: 2,
  picture: 3,
  verification: 5,
  seller_contact: 20, // $20 AUD to view seller contact
};

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [wallet, setWallet] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, quotesRes] = await Promise.all([
        walletAPI.getWallet(),
        quoteAPI.getQuotes()
      ]);
      setWallet(walletRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const hasCredits = (cost) => {
    return wallet && wallet.balance_credits >= cost;
  };

  const checkCreditsAndProceed = (action, cost, navigateTo) => {
    if (!wallet || wallet.balance_credits < cost) {
      setPendingAction({ action, cost, navigateTo });
      setShowCreditModal(true);
      return false;
    }
    return true;
  };

  const handleRequestQuote = () => {
    // Check if user has minimum credits for a quote
    const minCost = Math.min(CREDIT_COSTS.sea_quote, CREDIT_COSTS.air_quote);
    if (checkCreditsAndProceed('Request Quote', minCost, '/marketplace')) {
      navigate('/marketplace');
    }
  };

  const handleSearchSellers = () => {
    navigate('/marketplace');
  };

  const handleVerifyPhone = () => {
    if (checkCreditsAndProceed('Phone Verification', CREDIT_COSTS.verification, '/verify/whatsapp')) {
      navigate('/verify/whatsapp');
    }
  };

  const handleVerifyFace = () => {
    if (checkCreditsAndProceed('Face Verification', CREDIT_COSTS.verification, '/verify/face')) {
      navigate('/verify/face');
    }
  };

  const handleVerifyBusiness = () => {
    if (checkCreditsAndProceed('Business Verification', CREDIT_COSTS.verification, '/verify/business')) {
      navigate('/verify/business');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  const creditsBalance = wallet?.balance_credits || 0;
  const hasNoCredits = creditsBalance === 0;

  return (
    <div className="min-h-screen bg-surface" data-testid="buyer-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/servpac-logo.png" alt="ServPac" className="w-8 h-8 object-contain" />
              <h1 className="text-2xl font-chivo font-bold text-ocean-deep">ServPac</h1>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/marketplace')}
              >
                Browse Marketplace
              </Button>
              <span className="text-sm text-slate-600">Welcome, {user.full_name}</span>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-chivo font-bold text-slate-900 mb-2">Buyer Dashboard</h2>
          <p className="text-slate-600">Manage your quotes and credits</p>
        </div>

        {/* No Credits Warning */}
        {hasNoCredits && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800">No Credits Available</h4>
                <p className="text-sm text-amber-700 mt-1">
                  You need credits to request quotes, view seller contacts, send messages, and complete verification.
                  Purchase credits to unlock all features.
                </p>
                <Button
                  size="sm"
                  className="mt-3 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/checkout')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Buy Credits Now
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Credit Balance Card */}
          <Card data-testid="wallet-balance-card" className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-8 h-8 text-accent" />
            </div>
            <h3 className="text-lg font-inter font-medium text-slate-600 mb-1">Credit Balance</h3>
            <p className={`text-4xl font-chivo font-black ${creditsBalance === 0 ? 'text-red-500' : 'text-slate-900'}`}>
              {creditsBalance}
            </p>
            <Button
              data-testid="add-credits-btn"
              size="sm"
              className="mt-4 w-full"
              onClick={() => navigate('/checkout')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Credits
            </Button>
          </Card>

          {/* Search Sellers Card */}
          <Card data-testid="search-sellers-card" className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
            <Search className="w-8 h-8 text-blue-600 mb-2" />
            <h3 className="text-lg font-inter font-medium text-slate-700 mb-1">Find Sellers</h3>
            <p className="text-sm text-slate-600 mb-4">Browse Pacific Island exporters</p>
            <Button
              data-testid="search-sellers-btn"
              size="sm"
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={handleSearchSellers}
            >
              <Users className="w-4 h-4 mr-2" />
              Search Sellers
            </Button>
          </Card>

          {/* Quotes Card */}
          <Card data-testid="quotes-count-card" className="p-6">
            <FileText className="w-8 h-8 text-slate-900 mb-2" />
            <h3 className="text-lg font-inter font-medium text-slate-600 mb-1">Total Quotes</h3>
            <p className="text-4xl font-chivo font-black text-slate-900">{quotes.length}</p>
          </Card>

          {/* Verification Status Card */}
          <Card data-testid="verification-status-card" className="p-6">
            <Shield className="w-8 h-8 text-slate-900 mb-2" />
            <h3 className="text-lg font-inter font-medium text-slate-600 mb-2">Verification Progress</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {user.verified_email ? (
                    <Check className="w-4 h-4 text-green-600 mr-2" />
                  ) : (
                    <X className="w-4 h-4 text-red-600 mr-2" />
                  )}
                  <span>Email</span>
                </div>
                {user.verified_email && <span className="text-xs text-green-600">✓ Verified</span>}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {user.verified_phone ? (
                    <Check className="w-4 h-4 text-green-600 mr-2" />
                  ) : (
                    <X className="w-4 h-4 text-slate-400 mr-2" />
                  )}
                  <span>WhatsApp</span>
                </div>
                {user.verified_phone ? (
                  <span className="text-xs text-green-600">✓ Verified</span>
                ) : (
                  <span className="text-xs text-slate-400">Pending</span>
                )}
              </div>
              {user.business_type === 'individual' && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {user.verified_face ? (
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400 mr-2" />
                    )}
                    <span>Face ID</span>
                  </div>
                  {user.verified_face ? (
                    <span className="text-xs text-green-600">✓ Verified</span>
                  ) : (
                    <span className="text-xs text-slate-400">Pending</span>
                  )}
                </div>
              )}
              {user.business_type === 'business' && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {user.verified_business ? (
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400 mr-2" />
                    )}
                    <span>Business</span>
                  </div>
                  {user.verified_business ? (
                    <span className="text-xs text-green-600">✓ Verified</span>
                  ) : (
                    <span className="text-xs text-slate-400">Pending</span>
                  )}
                </div>
              )}
            </div>
            {/* Progress bar */}
            <div className="mt-3 pt-3 border-t">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500">Progress</span>
                <span className="font-medium text-slate-700">
                  {[
                    user.verified_email,
                    user.verified_phone,
                    user.business_type === 'individual' ? user.verified_face : user.verified_business
                  ].filter(Boolean).length} / 3
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ 
                    width: `${([
                      user.verified_email,
                      user.verified_phone,
                      user.business_type === 'individual' ? user.verified_face : user.verified_business
                    ].filter(Boolean).length / 3) * 100}%` 
                  }}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Credit Pricing Info */}
        <Card className="p-6 mb-8 bg-slate-50">
          <h3 className="text-lg font-chivo font-bold text-slate-900 mb-4">Credit Usage Guide</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{CREDIT_COSTS.sea_quote}</p>
              <p className="text-sm text-slate-600">Sea Freight Quote</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{CREDIT_COSTS.air_quote}</p>
              <p className="text-sm text-slate-600">Air Freight Quote</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-green-600">{CREDIT_COSTS.message}</p>
              <p className="text-sm text-slate-600">Per Message</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-amber-600">A$20</p>
              <p className="text-sm text-slate-600">Seller Contact</p>
            </div>
          </div>
        </Card>

        {/* Verification Section */}
        {(!user.verified_phone || (!user.verified_face && user.business_type === 'individual') || (!user.verified_business && user.business_type === 'business')) && (
          <Card className="p-6 mb-8">
            <h3 className="text-lg font-chivo font-bold text-slate-900 mb-4">Complete Your Verification</h3>
            <p className="text-sm text-slate-600 mb-4">
              Verification costs <span className="font-bold text-accent">{CREDIT_COSTS.verification} credits</span> per step. Verified buyers get better access to sellers.
            </p>
            <div className="flex flex-wrap gap-3">
              {!user.verified_phone && (
                <Button
                  data-testid="verify-whatsapp-btn"
                  className={`bg-green-600 hover:bg-green-700 ${!hasCredits(CREDIT_COSTS.verification) ? 'opacity-60' : ''}`}
                  onClick={handleVerifyPhone}
                >
                  {!hasCredits(CREDIT_COSTS.verification) && <Lock className="w-4 h-4 mr-2" />}
                  Verify Phone ({CREDIT_COSTS.verification} credits)
                </Button>
              )}
              {!user.verified_face && user.business_type === 'individual' && (
                <Button
                  data-testid="verify-face-btn"
                  variant="outline"
                  className={!hasCredits(CREDIT_COSTS.verification) ? 'opacity-60' : ''}
                  onClick={handleVerifyFace}
                >
                  {!hasCredits(CREDIT_COSTS.verification) && <Lock className="w-4 h-4 mr-2" />}
                  Verify Face ({CREDIT_COSTS.verification} credits)
                </Button>
              )}
              {!user.verified_business && user.business_type === 'business' && (
                <Button
                  data-testid="verify-business-btn"
                  variant="outline"
                  className={!hasCredits(CREDIT_COSTS.verification) ? 'opacity-60' : ''}
                  onClick={handleVerifyBusiness}
                >
                  {!hasCredits(CREDIT_COSTS.verification) && <Lock className="w-4 h-4 mr-2" />}
                  Verify Business ({CREDIT_COSTS.verification} credits)
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Recent Quotes */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-chivo font-bold text-slate-900">Recent Quotes</h3>
          <Button
            data-testid="create-quote-btn"
            onClick={handleRequestQuote}
            className={!hasCredits(CREDIT_COSTS.sea_quote) ? 'opacity-60' : ''}
          >
            {!hasCredits(CREDIT_COSTS.sea_quote) && <Lock className="w-4 h-4 mr-2" />}
            <Plus className="w-4 h-4 mr-2" />
            Request Quote ({CREDIT_COSTS.sea_quote}+ credits)
          </Button>
        </div>

        {quotes.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-slate-700 mb-2">No Quotes Yet</h4>
            <p className="text-slate-600 mb-6">
              Browse the marketplace, find products, and request shipping quotes.
            </p>
            <div className="flex justify-center gap-4">
              <Button onClick={handleSearchSellers}>
                <Search className="w-4 h-4 mr-2" />
                Find Sellers
              </Button>
              {hasCredits(CREDIT_COSTS.sea_quote) && (
                <Button variant="outline" onClick={() => navigate('/marketplace')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Browse Products
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {quotes.map((quote) => (
              <Card key={quote.id} data-testid={`quote-${quote.id}`} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        quote.freight_type === 'sea' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {quote.freight_type === 'sea' ? 'Sea Freight' : 'Air Freight'}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        quote.status === 'completed' ? 'bg-green-100 text-green-700' :
                        quote.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {quote.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {quote.origin} → {quote.destination}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {quote.cost_in_credits} credits used
                    </p>
                  </div>
                  <div className="text-right">
                    {quote.quoted_price && (
                      <p className="text-2xl font-bold text-accent">
                        ${quote.quoted_price.toFixed(2)}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      {new Date(quote.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Credit Required Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Credits Required</h3>
              <p className="text-slate-600 mb-4">
                You need <span className="font-bold text-accent">{pendingAction?.cost} credits</span> for {pendingAction?.action}.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Current balance: <span className="font-bold">{creditsBalance} credits</span>
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCreditModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    setShowCreditModal(false);
                    navigate('/checkout');
                  }}
                >
                  Buy Credits
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* WhatsApp Freight Assistance Widget */}
      <WhatsAppFreightWidget />
    </div>
  );
};

export default BuyerDashboard;
